import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useContext,
  useMemo,
  useLayoutEffect
} from 'react';
import {
  useAssetMVPermissions,
  useSelectedMVComment,
  useTypedSelector
} from '@hooks';
import type { fabric } from 'fabric';
import { useMixpanel } from 'react-mixpanel-browser';
import AccordionTopPanel from '@pages/MediaViewer/components/AccordionTopPanel/AccordionTopPanel';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { List, CollapsePanelProps, Skeleton } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import CommentEditor from '@pages/MediaViewer/Comments/components/CommentEditor';
import type { SubmitEditorValues } from '@pages/MediaViewer/Comments/components/CommentEditor/CommentEditor';
import {
  formatCommentsTree,
  TCommentItem
} from '@pages/MediaViewer/Comments/utils';
import { ReactComponent as CommentSvg } from '@assets/icons/comment.svg';
import { ReactComponent as CommentCloseSvg } from '@assets/icons/comment-close.svg';
import { ReactComponent as EmptySearchSvg } from '@assets/icons/empty-search.svg';
import { ReactComponent as WhiteArrowSvg } from '@assets/icons/arrow-right-without-line.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import classNames from 'classnames';
import CommentTask from '@pages/MediaViewer/Comments/components/CommentTask';
import {
  AssetItemDto,
  AssetVersionCommentDto,
  AssetVersionItemDto,
  AttachmentDto,
  MemberDto,
  TaskBoardItemDto
} from '@api/Api';
import {
  selectComment,
  setActivePageNumber,
  setCanvasMode,
  setDocumentHighlight,
  editComment as editCommentAction,
  createNewComment
} from '@redux/actions/mediaViewerAction';
import moment from 'moment';
import { toggleSingInExternalReviewerModal } from '@redux/actions/modalAction';
import './Comments.less';
import { useAuth } from '@hooks/useAuth';
import { nonNull } from '@helpers/non-null';
import {
  exportAsCSV,
  exportAsPlainText,
  exportAsXML
} from '@helpers/exportComments';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { parseStringWithLinksIntoHtmlLinks } from '@helpers/parseStringWithLinksIntoHtmlLinks';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { useOrganization } from '@components/OrganizationBoundary';
import {
  openTaskModal,
  setAssetListNeedRefresh,
  setListsNeedRefreshInCampaign
} from '@redux/reducers/tasks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useCreateTask } from '@hooks/tasks';
import { useFetch } from '@hooks/useFetch';
import {
  assetStateSelector,
  boardsSelector,
  campaignStateSelector
} from '@redux/selectors/tasks';
import {
  fetchAssetAssignees,
  fetchCampaignTaskBoards
} from '@redux/actions/tasks';
import CommentTree from '@pages/MediaViewer/Comments/CommentTree';
import { FabricContext } from '@context/FabricContext';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useQuery } from '@tanstack/react-query';
import { useDebounced } from '@helpers/useDebounced';
import useHasAnnotations from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useHasAnnotations';
import { useDeleteAttachment } from '@hooks/queries/attachment';
import { useMVCommentAttachUppy } from '@hooks/useMVCommentAttachUppy';
import { combineUserName } from '@helpers/combineUserName';
import {
  MediaViewerOutletContextState,
  UpdateAssetCommentParams,
  CreateAssetCommentParams
} from '@layouts/types';
import { AccordionFilterComments } from '@pages/MediaViewer/components/AccordionFilter';
import {
  DEFAULT_FILTER as COMMENTS_DEFAULT_FILTER,
  MY_COMMENT_FILTER
} from '@pages/MediaViewer/components/AccordionFilter/AccordionFilterComments';

type ICommentsProps = Omit<CollapsePanelProps, 'header'> & {
  selectedVersion: AssetVersionItemDto;
  asset: AssetItemDto;
  canCreateTask: boolean;
  commentMentions: boolean;
};

function Comments({
  canCreateTask,
  commentMentions,
  asset,
  selectedVersion,
  ...props
}: ICommentsProps) {
  const {
    addNewComment,
    removeComment,
    setCommentLike,
    setCommentResolve,
    editComment,
    getPotentialMentions,
    getPotentialGroupMentions,
    commentsList,
    isLoadingCommentsList,
    workspace
  } = useOutletContext<MediaViewerOutletContextState>();
  const { clearCanvas, canvas } = useContext(FabricContext);
  const mixpanel = useMixpanel();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useTypedDispatch();
  const { mutateAsync: deleteAttachment } = useDeleteAttachment({});
  const { currentOrganization } = useOrganization(false);
  const createTask = useCreateTask();
  const hasAnnotations = useHasAnnotations();
  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const canLeaveComment = useAssetMVPermissions('comment');
  const canReadComment = useAssetMVPermissions('readComments');
  const canViewTasks = useAssetMVPermissions('listTasks');

  const assetId = asset.id;
  const uppy = useMVCommentAttachUppy(assetId);
  const selectedCommentId = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedCommentId
  );
  const activePageNumber = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.activePageNumber
  );
  const canvasMode = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.canvasMode
  );
  const playerTime = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.playerTime
  );
  const [selectedUser, setSelectedUser] = useState<MemberDto | null>(null);
  const [selectedBoard, setSelectedBoard] = useState<TaskBoardItemDto | null>(
    null
  );
  const { campaignId } = asset;
  const { campaignName } = asset;
  const { workspaceId } = asset;
  const assetType = selectedVersion?.metadata.type;
  const isAudio = assetType === 'video' || assetType === 'audio';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const isImage =
    assetType === 'camera_raw_image' ||
    assetType === 'raster_image' ||
    assetType === 'vector_image';
  const checkboxValueType =
    (isDocument && 'pageNumber') || (isAudio && 'timeCode') || null;

  useFetch({
    key: `campaign-task-board-${campaignId}`,
    disabled: user?.type !== 'internal' || !campaignId || !canViewTasks,
    selector: (state) =>
      campaignStateSelector(state, { campaignId }).boards.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchCampaignTaskBoards({
          campaignId,
          fetchType
        })
      );
    }
  });
  useEffect(() => {
    dispatch(setListsNeedRefreshInCampaign({ campaignId }));
  }, [campaignId, dispatch]);

  useFetch({
    key: `asset-assignees-${assetId}`,
    disabled: user?.type !== 'internal' || !assetId || !canViewTasks,
    selector: (state) =>
      assetStateSelector(state, { assetId }).potentialAssignees.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchAssetAssignees({
          workspaceId,
          assetId,
          fetchType
        })
      );
    }
  });
  useEffect(() => {
    dispatch(setAssetListNeedRefresh({ assetId }));
  }, [assetId, dispatch]);

  const taskBoards = useTypedSelector((state) =>
    boardsSelector(state, { campaignId })
  ).filter((x) => x.id !== 'archived' && x.id !== 'no_assigned');
  const potentialAssignees = useTypedSelector(
    (state) => assetStateSelector(state, { assetId }).potentialAssignees.list
  );

  const [isShowTask, setIsShowTask] = useState<boolean>(false);
  const [editCommentId, setEditCommentId] = useState<string | null>(null);
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(true);
  const [isUnresolvedComments, setIsUnresolvedComments] =
    useState<boolean>(false);
  const [sortSelected, setSortSelected] = useState<string>('');
  const [showReplyOfCommentId, setShowReplyOfCommentId] = useState<
    string | null
  >(null);
  const [filterCommentsSelected, setFilterCommentsSelected] = useState<
    string[]
  >([COMMENTS_DEFAULT_FILTER]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const queryCommentId = searchParams.get('commentId');
  const selectedComment = useSelectedMVComment();
  useOnClickOutside(commentsContainerRef, (e) => {
    if (
      selectedComment?.timeCode?.toSeconds &&
      (e.target.closest('.video_control_bar_container') ||
        e.target.closest('.video_control_bar_track'))
    )
      return;
    if (
      canvasMode === 'view' &&
      selectedCommentId !== 'new' &&
      selectedCommentId !== 'reply' &&
      !e.target.classList.contains('upper-canvas') &&
      !e.target.classList.contains('toggle_open') &&
      !e.target.closest('.media_viewer_panel-action-btn') &&
      !e.target.closest('.media_viewer_panel-action-slide')
    ) {
      dispatch(setCanvasMode('view'));
      dispatch(selectComment(null));
      dispatch(setDocumentHighlight(null));
    }
  });

  const dropdownMenuItems: ItemType[] = [
    {
      label: (
        <UpgradePlanTooltip
          text=" to download comments."
          isCanVisible={!currentOrganization?.entity?.features.downloadComments}
        >
          <span>Export comments as</span>
        </UpgradePlanTooltip>
      ),
      expandIcon: currentOrganization?.entity?.features.downloadComments ? (
        <WhiteArrowSvg />
      ) : (
        <UpgradePlanSvg style={{ marginLeft: 4 }} />
      ),
      disabled: !currentOrganization?.entity?.features.downloadComments,
      key: 'submenu',
      popupClassName: 'media_viewer_submenu media_viewer_mobile-overlay',
      popupOffset: [12, 0],
      onTitleClick: ({ domEvent }) => domEvent.stopPropagation(),
      children: [
        { label: 'CSV', key: 'CSV' },
        { label: 'XML', key: 'XML' },
        { label: 'Plain text', key: 'Plain text' }
      ]
    }
  ];

  const onExport = (value: 'CSV' | 'XML' | 'Plain text') => {
    if (!workspace || !asset || !selectedVersion || !formattedComments) return;
    if (value === 'Plain text')
      exportAsPlainText(selectedVersion, formattedComments);
    if (value === 'CSV')
      exportAsCSV(workspace.name, asset, selectedVersion, commentsList);
    if (value === 'XML')
      exportAsXML(workspace.name, asset, selectedVersion, formattedComments);
  };

  const formattedComments = useCallback(() => {
    let list = formatCommentsTree(commentsList);
    if (list.length <= 0) return [];

    if (searchQuery) {
      list = list.filter(({ text }: { text: string }) =>
        text.includes(searchQuery)
      );
    }

    if (isUnresolvedComments) {
      list = list.filter((el) => !el.resolvedAt);
    }

    if (
      filterCommentsSelected.length &&
      !filterCommentsSelected.includes(COMMENTS_DEFAULT_FILTER)
    ) {
      const isMyComments = filterCommentsSelected.includes(MY_COMMENT_FILTER);
      const isMatch = (item: TCommentItem | undefined) =>
        item &&
        (isMyComments
          ? item.user?.me
          : filterCommentsSelected.includes(item.user?.id || ''));
      list = list.reduce<TCommentItem[]>((acc, el) => {
        const existUser = isMatch(el);
        const filteredChildren = el.children?.filter(isMatch);
        if (existUser || filteredChildren?.length) {
          acc.push({
            ...el,
            children: filteredChildren
          });
        }
        return acc;
      }, []);
    }

    if (sortSelected === 'Priority')
      list = list.sort((x: any, y: any) =>
        x.priority === y.priority ? 0 : x ? -1 : 1
      );

    if (sortSelected === 'Date created')
      list = list.sort(
        (x: any, y: any) =>
          +moment(y.createdAt).format('X') - +moment(x.createdAt).format('X')
      );

    if (sortSelected === 'Date modified')
      list = list.sort(
        (x: any, y: any) =>
          (+moment(y.updatedAt).format('X') || 0) -
          (+moment(x.updatedAt).format('X') || 0)
      );

    if (sortSelected === 'Page') {
      list = list.sort((x: any, y: any) => {
        if (!!x.documentPage && !!y.documentPage)
          return x.documentPage - y.documentPage;
        if (!x.documentPage && !y.documentPage) return 0;
        return !x.documentPage ? 1 : -1;
      });
    }

    if (sortSelected === 'Timecode') {
      list = list.sort((x: any, y: any) => {
        const xTime = x.timeCode
          ? x.timeCode.fromSeconds
          : +moment(x.createdAt).format('X');
        const yTime = y.timeCode
          ? y.timeCode.fromSeconds
          : +moment(y.createdAt).format('X');

        return xTime - yTime;
      });
    }
    return list;
  }, [
    commentsList,
    isUnresolvedComments,
    filterCommentsSelected,
    sortSelected,
    searchQuery
  ])();

  useEffect(() => {
    if (!commentsList.length) return;
    if (!queryCommentId) return;
    const repliedCommentId = commentsList.find(
      (el) => el.id === queryCommentId
    )?.repliedToId;
    const commentPage = commentsList.find(
      (it) => it.id === queryCommentId
    )?.documentPage;
    if (commentPage) {
      dispatch(setActivePageNumber(commentPage));
    }
    if (repliedCommentId) {
      setShowReplyOfCommentId(repliedCommentId);
      if (showReplyOfCommentId)
        document
          .querySelector(
            `.media_viewer_comments_container [data-comment-id="${queryCommentId}"]`
          )
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
    } else {
      document
        .querySelector(
          `.media_viewer_comments_container [data-comment-id="${queryCommentId}"]`
        )
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
    }
    searchParams.delete('commentId');
    setSearchParams(searchParams);
    dispatch(selectComment(queryCommentId));
  }, [commentsList, queryCommentId]);

  useEffect(() => {
    if (selectedCommentId !== 'new' && canvasMode !== 'edit') {
      setEditCommentId(null);
    }
  }, [selectedCommentId, canvasMode]);

  useLayoutEffect(() => {
    setFilterCommentsSelected([COMMENTS_DEFAULT_FILTER]);
    setIsUnresolvedComments(false);
  }, [selectedVersion?.id, setIsUnresolvedComments, setFilterCommentsSelected]);

  useEffect(() => {
    if (selectedCommentId === 'new') setEditCommentId('new');
    const topParentId = commentsList.find(
      (it) => selectedCommentId === it.id
    )?.topParentId;
    if (topParentId) setShowReplyOfCommentId(topParentId);
    if (selectedCommentId) {
      document
        .querySelector(
          `.media_viewer_comments_container [data-comment-id="${selectedCommentId}"]`
        )
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
    }
  }, [selectedCommentId, commentsList, showReplyOfCommentId]);

  const onTurnCommentIntoTask = async (
    comment: AssetVersionCommentDto,
    selectedBoard?: TaskBoardItemDto | null,
    selectedUser?: MemberDto | null
  ) => {
    try {
      let parsedText = comment.text;
      let parsedName = comment.text
        .replace(/<br[^>]*>/gi, ' ')
        .replace(/<[^>]*>?/gm, '');
      comment.mentions.forEach((el) => {
        parsedName = parsedName.replaceAll(`{{${el.id}}}`, '');
        parsedText = parsedText.replaceAll(
          `{{${el.id}}}`,
          `<span data-type="mention" class="b-suggestion-user" data-label="${combineUserName(
            el
          )}" contenteditable="false">@${combineUserName(el)}</span>`
        );
      });
      comment.groupMentions.forEach((el) => {
        parsedName = parsedName.replaceAll(`{{${el.id}}}`, '');
        parsedText = parsedText.replaceAll(
          `{{${el.id}}}`,
          `<span data-type="mention" class="b-suggestion-user" data-label="${el.name}" contenteditable="false">@${el.name}</span>`
        );
      });
      parsedName = parsedName.replaceAll(
        /{{(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})}}/g,
        ``
      );
      if (parsedName.length > 60) parsedName = `${parsedName.slice(0, 57)}...`;
      parsedText = parsedText.replaceAll(
        /{{(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})}}/g,
        `<span data-type="mention" class="b-suggestion-user" data-label="Deleted User" contenteditable="false">@Deleted User</span>`
      );

      const boardId = selectedBoard?.id;
      const { task } = await createTask({
        workspaceId,
        campaign: {
          id: campaignId,
          name: campaignName
        },
        name: parsedName.trim() || 'New task',
        description: parseStringWithLinksIntoHtmlLinks(parsedText),
        assetVersionCommentId: comment.id,
        assignees: selectedUser
          ? [{ ...selectedUser, hasAccessToTask: true }]
          : undefined,
        board: {
          id: boardId || 'no_assigned'
        },
        taskPosition: 'start-of-list'
      }).unwrap();
      if (user) {
        MixpanelService.track(
          mixpanel,
          user.id,
          MixpanelEventType.MANAGE_ASSETS,
          {
            addToFavorite: false,
            deleteAsset: false,
            newAsset: false,
            externalLinkShare: false,
            approvalRouting: false,
            newCommentAsset: false,
            annotationLeft: false,
            taskFromComment: true
          }
        );
      }
      editCommentAction({
        ...comment,
        task: { id: task.id }
      });
      dispatch(
        openTaskModal({
          taskId: task.id,
          descriptionPlaceholder:
            'Describe your task so your teammates know what to do.'
        })
      );
    } catch (error) {
      console.warn(error, '-> create task');
    }
  };

  const onAddComment = async ({
    text,
    rawText,
    mentions,
    groupMentions,
    idOrParentId: parentId,
    documentHighlight,
    documentHighlightRects,
    documentHighlightReplacement
  }: SubmitEditorValues) => {
    const timeCode = {
      fromSeconds: playerTime[0] || 0,
      toSeconds: playerTime[1]
    };
    const documentPage = activePageNumber;
    let data: Partial<CreateAssetCommentParams> = {
      text: parseStringWithLinksIntoHtmlLinks(text),
      annotations: getAnnotations(),
      mentions,
      groupMentions,
      rawText
    };
    if (isCheckboxChecked && isAudio) {
      data = {
        ...data,
        timeCode
      };
    }
    if (isCheckboxChecked && isDocument) {
      data = {
        ...data,
        documentPage,
        documentHighlight,
        documentHighlightRects,
        documentHighlightReplacement
      };
    }
    if (parentId && parentId !== 'new') {
      const parentComment = commentsList.find((it) => it.id === parentId);
      let replyToRawText = parentComment?.text;
      parentComment?.mentions.forEach((el: any) => {
        replyToRawText = parentComment.text.replace(
          `{{${el.id}}}`,
          `@${combineUserName(el)}`
        );
      });
      parentComment?.groupMentions.forEach((el: any) => {
        replyToRawText = parentComment.text.replace(
          `{{${el.id}}}`,
          `@${el.name}`
        );
      });
      data = {
        ...data,
        replyToId: parentId,
        replyToRawText
      };
    } else
      data = {
        ...data,
        assetVersionId: selectedVersion?.id || ''
      };
    const comment = await addNewComment(
      data as CreateAssetCommentParams,
      parentId
    );
    const files = uppy.getFiles();
    if (files.length) {
      files.forEach((item) => {
        uppy.setFileMeta(item.id, {
          commentId: comment.id,
          readyForUpload: true
        });
      });
      await uppy.upload();
    }
    await createNewComment(
      {
        ...comment,
        attachments: files.map((file) => ({
          id: (file.meta.attachmentId || file.id) as string,
          name: file.name || '',
          fileType: file.type as AttachmentDto['fileType'],
          mimeType: file.type,
          size: file.size || 0,
          url: URL.createObjectURL(file.data) || '',
          urlExpiresAt: (file.meta.relativePath || '') as string
        }))
      },
      selectedVersion?.id || ''
    );
    clearCanvas();
    requestAnimationFrame(() => {
      document
        .querySelector(
          `.media_viewer_comments_container [data-comment-id="${comment.id}"]`
        )
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
    });
    dispatch(setCanvasMode('view'));
    dispatch(selectComment(null));
    dispatch(setDocumentHighlight(null));
    if (isShowTask) {
      onTurnCommentIntoTask(comment, selectedBoard, selectedUser);
    }
  };

  const onDeleteComment = useCallback(
    async (id: string) => {
      await removeComment(id);
    },
    [removeComment]
  );

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (
        e.key === 'Delete' &&
        selectedComment?.id &&
        canvasMode === 'view' &&
        selectedComment.user?.me
      ) {
        dispatch(selectComment(null));

        onDeleteComment(selectedComment.id);
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [
    dispatch,
    canvasMode,
    onDeleteComment,
    selectedComment?.id,
    selectedComment?.user?.me
  ]);

  const onToggleLikeComment = async ({
    id,
    value
  }: {
    id: string;
    value: boolean;
  }) => {
    const {
      id: userId,
      type,
      pictureUrl,
      email,
      firstName,
      lastName
    } = nonNull(user);
    await setCommentLike(
      { id, value },
      {
        id: userId,
        type: type === 'internal' ? 'user' : 'external-reviewer',
        picture: { url: pictureUrl ?? null },
        email,
        firstName,
        lastName: lastName || '',
        me: true
      }
    );
  };

  const onToggleResolveComment = async ({
    id,
    value
  }: {
    id: string;
    value: boolean;
  }) => {
    const {
      id: userId,
      type,
      pictureUrl,
      email,
      firstName,
      lastName
    } = nonNull(user);
    await setCommentResolve(
      { id, value },
      {
        id: userId,
        type: type === 'internal' ? 'user' : 'external-reviewer',
        picture: { url: pictureUrl ?? null },
        email,
        firstName,
        lastName: lastName || '',
        me: true
      }
    );
  };

  const onEditComment = async ({
    text,
    rawText,
    mentions,
    groupMentions,
    idOrParentId: id,
    documentHighlight,
    documentHighlightRects,
    documentHighlightReplacement,
    attachmentsLeft
  }: SubmitEditorValues) => {
    const comment = commentsList.find((it) => it.id === id);
    if (!comment) return;
    const data: UpdateAssetCommentParams = {
      id,
      text: parseStringWithLinksIntoHtmlLinks(text),
      annotations: getAnnotations(),
      rawText,
      mentions: {
        add: mentions.filter(
          (id) => !comment.mentions.some((x) => x.id === id)
        ),
        remove: comment.mentions
          .filter((x) => !mentions.includes(x.id))
          .map((i) => i.id)
      },
      groupMentions: {
        add: groupMentions.filter(
          (id) => !comment.groupMentions.some((x) => x.id === id)
        ),
        remove: comment.groupMentions
          .filter((x) => !groupMentions.includes(x.id))
          .map((i) => i.id)
      },
      timeCode: isAudio
        ? isCheckboxChecked
          ? {
              fromSeconds: playerTime[0] || 0,
              toSeconds: playerTime[1]
            }
          : null
        : undefined,
      documentHighlight,
      documentHighlightRects,
      documentHighlightReplacement
    };
    const files = uppy.getFiles();
    const attachmentIdsLeft = (attachmentsLeft || []).map((item) => item.id);
    const removeFilesPromise = (comment?.attachments || [])
      .filter((attachment) => !attachmentIdsLeft.includes(attachment.id))
      .map((attachment) =>
        deleteAttachment({
          id: attachment.id
        })
      );
    files.forEach((item) => {
      uppy.setFileMeta(item.id, {
        commentId: comment.id,
        readyForUpload: true
      });
    });
    if (removeFilesPromise.length) await Promise.all(removeFilesPromise);
    if (files.length) await uppy.upload();
    await editComment(data);
    clearCanvas();
    dispatch(setDocumentHighlight(null));
    dispatch(setCanvasMode('view'));
  };

  const getAnnotations = (): string | null => {
    if (!canvas) return null;
    const json = canvas.toJSON(['assetAngle', 'userEmail', 'userId']) as any;
    const getObjects = json.objects.filter(
      (item: fabric.Object) => item.type !== 'highlight'
    );
    if (!getObjects.length || !hasAnnotations) return null;
    return JSON.stringify(getObjects);
  };

  const [potentialMentionsSearchQuery, setPotentialMentionsSearchQuery] =
    useState<string>('');
  const debouncedPotentialMentionsSearchQuery = useDebounced(
    potentialMentionsSearchQuery,
    300
  );
  const potentialMentionsQuery = useQuery({
    queryKey: [
      'asset',
      'comment',
      'potential-mentions',
      selectedVersion.id,
      debouncedPotentialMentionsSearchQuery
    ],
    keepPreviousData: true,
    queryFn: () =>
      getPotentialMentions({
        assetVersionId: selectedVersion.id,
        searchQuery: potentialMentionsSearchQuery
      })
  });
  const potentialMentions = useMemo(
    () => potentialMentionsQuery.data ?? [],
    [potentialMentionsQuery.data]
  );
  const potentialGroupMentionsQuery = useQuery({
    queryKey: [
      'asset',
      'comment',
      'potential-group-mentions',
      selectedVersion.id,
      debouncedPotentialMentionsSearchQuery
    ],
    keepPreviousData: true,
    queryFn: () =>
      getPotentialGroupMentions({
        assetVersionId: selectedVersion.id,
        searchQuery: potentialMentionsSearchQuery
      })
  });
  const potentialGroupMentions = useMemo(
    () => potentialGroupMentionsQuery.data ?? [],
    [potentialGroupMentionsQuery.data]
  );

  const canExternalMakeAction = (callBack: any) => {
    if (!user) {
      dispatch(
        toggleSingInExternalReviewerModal({
          visible: true
        })
      );
      return;
    }
    callBack();
  };

  return (
    <AccordionTopPanel
      {...props}
      name="Comments"
      dropdownMenu={commentsList?.length ? dropdownMenuItems : null}
      onDropdownHandler={onExport}
      count={commentsList?.length}
    >
      <>
        {canReadComment && (
          <AccordionFilterComments
            commentsList={commentsList}
            onSortHandler={setSortSelected}
            dropdownValue={filterCommentsSelected}
            onDropdownHandler={setFilterCommentsSelected}
            onSearchHandler={(v) => setSearchQuery(v)}
            exportMenu={dropdownMenuItems}
            onExportHandler={onExport}
            isUnresolved={isUnresolvedComments}
            onUnResolveHandler={setIsUnresolvedComments}
            assetType={isAudio ? 'audio' : isDocument ? 'document' : undefined}
          />
        )}

        <div
          className="media_viewer_comments_container"
          ref={commentsContainerRef}
        >
          <div className="media_viewer_comments_top">
            {canLeaveComment && (
              <CommentEditor
                commentMentions={commentMentions}
                editCommentId={editCommentId}
                setEditCommentId={setEditCommentId}
                editorFor="new"
                autoFocus={false}
                isFocused={editCommentId === 'new'}
                replyToCommentId={replyToCommentId}
                setReplyToCommentId={setReplyToCommentId}
                onSubmitEditor={onAddComment}
                isCheckboxChecked={isCheckboxChecked}
                setIsCheckboxChecked={setIsCheckboxChecked}
                checkboxValueType={checkboxValueType}
                potentialMentions={potentialMentions}
                potentialGroupMentions={potentialGroupMentions}
                searchPotentialMentions={(v) =>
                  setPotentialMentionsSearchQuery(v)
                }
                uppy={uppy}
              />
            )}
            {user?.type === 'internal' && editCommentId === 'new' && (
              <CommentTask
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                selectedBoard={selectedBoard}
                setSelectedBoard={setSelectedBoard}
                isShowTask={isShowTask}
                setIsShowTask={setIsShowTask}
                potentialAssignees={potentialAssignees}
                boardList={
                  selectedVersion?.versionNumber === asset?.asset?.versionsCount
                    ? taskBoards
                    : []
                }
                canCreateTask={canCreateTask}
              />
            )}
          </div>
          {isLoadingCommentsList && (
            <List
              className="media_viewer_comments_list-skeleton"
              itemLayout="horizontal"
              bordered={false}
              split={false}
              dataSource={new Array(10)
                .fill(undefined)
                .map((_, i) => ({ key: i }))}
              renderItem={() => (
                <List.Item>
                  <Skeleton.Input style={{ height: 88 }} active />
                </List.Item>
              )}
            />
          )}

          {!isLoadingCommentsList && (
            <List
              className={classNames(
                'media_viewer_comments_list',
                !formattedComments.length && 'media_viewer_comments_list-empty'
              )}
              dataSource={formattedComments}
              rowKey="id"
              itemLayout="horizontal"
              renderItem={(comment) => (
                <CommentTree
                  commentMentions={commentMentions}
                  comment={comment}
                  editCommentId={editCommentId}
                  selectedCommentId={selectedCommentId}
                  replyToCommentId={replyToCommentId}
                  showReplyOfCommentId={showReplyOfCommentId}
                  isCheckboxChecked={isCheckboxChecked}
                  checkboxValueType={checkboxValueType}
                  onReply={(comment) => {
                    canExternalMakeAction(() => {
                      setEditCommentId(null);
                      dispatch(setDocumentHighlight(null));
                      if (replyToCommentId === comment.id) {
                        dispatch(setCanvasMode('view'));
                        setReplyToCommentId(null);
                        dispatch(selectComment(null));
                      } else {
                        setReplyToCommentId(comment.id);
                        setShowReplyOfCommentId(
                          comment.topParentId || comment.id
                        );
                        dispatch(setCanvasMode('edit'));
                        dispatch(selectComment('reply'));
                      }
                    });
                  }}
                  onLikeClick={(comment) => {
                    canExternalMakeAction(() => {
                      const hasLike = comment.likes.find((el: any) => el.me);
                      onToggleLikeComment({ value: !hasLike, id: comment.id });
                    });
                  }}
                  onResolveClick={(comment) => {
                    canExternalMakeAction(() => {
                      onToggleResolveComment({
                        value: !comment.resolvedAt,
                        id: comment.id
                      });
                    });
                  }}
                  onShowReplyClick={(comment) => {
                    if (showReplyOfCommentId === comment.id)
                      setShowReplyOfCommentId(null);
                    else setShowReplyOfCommentId(comment.id);
                    setReplyToCommentId(null);
                  }}
                  potentialMentions={potentialMentions}
                  potentialGroupMentions={potentialGroupMentions}
                  searchPotentialMentions={(v) =>
                    setPotentialMentionsSearchQuery(v)
                  }
                  onAddComment={onAddComment}
                  setEditCommentId={setEditCommentId}
                  setReplyToCommentId={setReplyToCommentId}
                  setIsCheckboxChecked={setIsCheckboxChecked}
                  onEditComment={onEditComment}
                  onDeleteComment={onDeleteComment}
                  onTurnCommentIntoTask={onTurnCommentIntoTask}
                  canCreateTask={canCreateTask}
                  uppy={uppy}
                />
              )}
              locale={{
                emptyText: !canReadComment ? (
                  <div className="media_viewer_comments_empty">
                    <CommentCloseSvg className="b-comment-icon" />
                    You have view-only permission
                  </div>
                ) : searchQuery && !formattedComments.length ? (
                  <div className="media_viewer_comments_empty">
                    <EmptySearchSvg className="b-comment-icon" />
                    <span className="title">No results found</span>
                    It seems we can’t find any results based on your search
                  </div>
                ) : (
                  <div className="media_viewer_comments_empty">
                    <CommentSvg className="b-comment-icon" />
                    No comments yet
                  </div>
                )
              }}
            />
          )}
        </div>
      </>
    </AccordionTopPanel>
  );
}

export default Comments;
